
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { Plus, Trash2, Save, Eye, EyeOff } from 'lucide-react'

async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export default function Admin() {
  const [activeSection, setActiveSection] = useState('courses')
  const [courses, setCourses] = useState([])
  const [points, setPoints] = useState([])
  const [coursePoints, setCoursePoints] = useState([])
  const [detections, setDetections] = useState([])
  const [loading, setLoading] = useState(true)
  const [horairesEdits, setHorairesEdits] = useState({})

  const [newCourse, setNewCourse] = useState({ nom: '', description: '', lot_coureur: false, lot_tombola: false })
  const [newPoint, setNewPoint] = useState({ nom: '', nom_feuille: '', heure_ouverture: '', heure_fermeture: '' })
  const [newCoursePoint, setNewCoursePoint] = useState({ course_id: '', point_chrono_id: '', ordre: 1 })

  // Securite
  const [pinBenevole, setPinBenevole] = useState('')
  const [pinAdmin, setPinAdmin] = useState('')
  const [pinAdminConfirm, setPinAdminConfirm] = useState('')
  const [showPinB, setShowPinB] = useState(false)
  const [showPinA, setShowPinA] = useState(false)
  const [pinMsg, setPinMsg] = useState(null)
  const [savingPin, setSavingPin] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    const [c, p, cp, d] = await Promise.all([
      supabase.from('courses').select('*').order('nom'),
      supabase.from('points_chrono').select('*').order('nom'),
      supabase.from('course_points').select('*, courses(nom), points_chrono(nom)').order('ordre'),
      supabase.from('detections').select('*'),
    ])
    setCourses(c.data || [])
    setPoints(p.data || [])
    setCoursePoints(cp.data || [])
    setDetections(d.data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  useEffect(() => {
    if (points.length === 0) return
    setHorairesEdits(prev => {
      const edits = { ...prev }
      points.forEach(p => {
        if (!edits[p.id]) {
          edits[p.id] = {
            heure_ouverture: toDatetimeLocal(p.heure_ouverture),
            heure_fermeture: toDatetimeLocal(p.heure_fermeture),
          }
        }
      })
      return edits
    })
  }, [points])

  function toDatetimeLocal(ts) {
    if (!ts) return ''
    const d = new Date(ts)
    const pad = n => String(n).padStart(2, '0')
    return d.getFullYear() + '-' + pad(d.getMonth()+1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes())
  }

  function getHoraireEdit(pointId, field) {
    return horairesEdits[pointId]?.[field] ?? ''
  }

  function setHoraireEdit(pointId, field, value) {
    setHorairesEdits(prev => ({
      ...prev,
      [pointId]: { ...prev[pointId], [field]: value }
    }))
  }

  function horaireModifie(pointId) {
    const point = points.find(p => p.id === pointId)
    if (!point) return false
    return (
      (horairesEdits[pointId]?.heure_ouverture ?? '') !== toDatetimeLocal(point.heure_ouverture) ||
      (horairesEdits[pointId]?.heure_fermeture ?? '') !== toDatetimeLocal(point.heure_fermeture)
    )
  }

  async function sauvegarderHoraires(pointId) {
    const editOuverture = horairesEdits[pointId]?.heure_ouverture
    const editFermeture = horairesEdits[pointId]?.heure_fermeture
    const newOuverture = editOuverture ? new Date(editOuverture).toISOString() : null
    const newFermeture = editFermeture ? new Date(editFermeture).toISOString() : null

    const detectionsPoint = detections.filter(d =>
      d.point_chrono_id === pointId &&
      d.source === 'google_sheet' &&
      !d.forcee
    )

    const aInvalider = detectionsPoint.filter(d => {
      if (d.invalide) return false
      const ts = new Date(d.timestamp)
      if (newOuverture && ts < new Date(newOuverture)) return true
      if (newFermeture && ts > new Date(newFermeture)) return true
      return false
    })

    const aRevalider = detectionsPoint.filter(d => {
      if (!d.invalide) return false
      if (d.motif_invalidation !== 'Hors fenetre horaire') return false
      const ts = new Date(d.timestamp)
      if (newOuverture && ts < new Date(newOuverture)) return false
      if (newFermeture && ts > new Date(newFermeture)) return false
      return true
    })

    if (aInvalider.length > 0 || aRevalider.length > 0) {
      let msg = 'Cette modification va :\n'
      if (aInvalider.length > 0) msg += '- Invalider ' + aInvalider.length + ' detection(s) hors fenetre\n'
      if (aRevalider.length > 0) msg += '- Revalider ' + aRevalider.length + ' detection(s) dans la nouvelle fenetre\n'
      msg += '\nConfirmer ?'
      if (!window.confirm(msg)) return
    }

    const now = new Date().toISOString()
    for (const det of aInvalider) {
      await supabase.from('detections').update({
        invalide: true, motif_invalidation: 'Hors fenetre horaire',
        invalide_par: 'systeme', invalide_le: now
      }).eq('id', det.id)
    }
    for (const det of aRevalider) {
      await supabase.from('detections').update({
        invalide: false, motif_invalidation: null, invalide_par: null, invalide_le: null
      }).eq('id', det.id)
    }

    await supabase.from('points_chrono').update({
      heure_ouverture: newOuverture,
      heure_fermeture: newFermeture,
    }).eq('id', pointId)

    setHorairesEdits(prev => ({
      ...prev,
      [pointId]: { heure_ouverture: editOuverture, heure_fermeture: editFermeture }
    }))
    fetchAll()
  }

  async function addCourse() {
    if (!newCourse.nom) return
    await supabase.from('courses').insert(newCourse)
    setNewCourse({ nom: '', description: '', lot_coureur: false, lot_tombola: false })
    fetchAll()
  }

  async function updateCourse(id, field, value) {
    await supabase.from('courses').update({ [field]: value }).eq('id', id)
    fetchAll()
  }

  async function deleteCourse(id) {
    if (!window.confirm('Supprimer cette course ?')) return
    await supabase.from('courses').delete().eq('id', id)
    fetchAll()
  }

  async function addPoint() {
    if (!newPoint.nom || !newPoint.nom_feuille) return
    await supabase.from('points_chrono').insert({
      nom: newPoint.nom,
      nom_feuille: newPoint.nom_feuille,
      heure_ouverture: newPoint.heure_ouverture ? new Date(newPoint.heure_ouverture).toISOString() : null,
      heure_fermeture: newPoint.heure_fermeture ? new Date(newPoint.heure_fermeture).toISOString() : null,
    })
    setNewPoint({ nom: '', nom_feuille: '', heure_ouverture: '', heure_fermeture: '' })
    fetchAll()
  }

  async function deletePoint(id) {
    if (!window.confirm('Supprimer ce point chrono ?')) return
    await supabase.from('points_chrono').delete().eq('id', id)
    fetchAll()
  }

  async function addCoursePoint() {
    if (!newCoursePoint.course_id || !newCoursePoint.point_chrono_id) return
    await supabase.from('course_points').insert(newCoursePoint)
    setNewCoursePoint({ course_id: '', point_chrono_id: '', ordre: 1 })
    fetchAll()
  }

  async function updateCoursePointOrdre(id, ordre) {
    await supabase.from('course_points').update({ ordre: parseInt(ordre) }).eq('id', id)
    fetchAll()
  }

  async function deleteCoursePoint(id) {
    await supabase.from('course_points').delete().eq('id', id)
    fetchAll()
  }

  async function sauvegarderPin(cle, valeur, confirm) {
    if (valeur.length < 4) { setPinMsg({ type:'error', text:'PIN trop court (minimum 4 chiffres)' }); return }
    if (confirm !== undefined && valeur !== confirm) { setPinMsg({ type:'error', text:'Les PINs ne correspondent pas' }); return }
    setSavingPin(true)
    const hash = await sha256(valeur)
    const { error } = await supabase.from('config').upsert({ cle, valeur: hash }, { onConflict: 'cle' })
    if (error) setPinMsg({ type:'error', text:'Erreur : ' + error.message })
    else {
      setPinMsg({ type:'success', text:'PIN mis a jour !' })
      if (cle === 'pin_benevole') setPinBenevole('')
      else { setPinAdmin(''); setPinAdminConfirm('') }
    }
    setSavingPin(false)
    setTimeout(() => setPinMsg(null), 3000)
  }

  if (loading) return <div className="loading">Chargement...</div>

  return (
    <div className="admin">
      <h1>Administration</h1>

      {/* ONGLETS ADMIN */}
      <div className="dash-onglets" style={{marginBottom:16}}>
        {['courses','points','liaisons','securite'].map(s => (
          <button key={s} className={'dash-onglet ' + (activeSection === s ? 'active' : '')} onClick={() => setActiveSection(s)}>
            {s === 'courses' ? 'Courses' : s === 'points' ? 'Points chrono' : s === 'liaisons' ? 'Points par course' : 'Securite'}
          </button>
        ))}
      </div>

      {/* COURSES */}
      {activeSection === 'courses' && (
        <div className="section">
          <div className="section-body">
            <table className="table">
              <thead>
                <tr><th>Nom</th><th>Description</th><th>Lot coureur</th><th>Lot tombola</th><th></th></tr>
              </thead>
              <tbody>
                {courses.map(c => (
                  <tr key={c.id}>
                    <td><input defaultValue={c.nom} onBlur={e => { if (e.target.value !== c.nom) updateCourse(c.id, 'nom', e.target.value) }} /></td>
                    <td><input defaultValue={c.description || ''} onBlur={e => { if (e.target.value !== (c.description || '')) updateCourse(c.id, 'description', e.target.value) }} /></td>
                    <td><input type="checkbox" checked={c.lot_coureur} onChange={e => updateCourse(c.id, 'lot_coureur', e.target.checked)} /></td>
                    <td><input type="checkbox" checked={c.lot_tombola} onChange={e => updateCourse(c.id, 'lot_tombola', e.target.checked)} /></td>
                    <td><button className="btn-icon danger" onClick={() => deleteCourse(c.id)}><Trash2 size={14}/></button></td>
                  </tr>
                ))}
                <tr className="add-row">
                  <td><input placeholder="Nom *" value={newCourse.nom} onChange={e => setNewCourse({...newCourse, nom: e.target.value})} /></td>
                  <td><input placeholder="Description" value={newCourse.description} onChange={e => setNewCourse({...newCourse, description: e.target.value})} /></td>
                  <td><input type="checkbox" checked={newCourse.lot_coureur} onChange={e => setNewCourse({...newCourse, lot_coureur: e.target.checked})} /></td>
                  <td><input type="checkbox" checked={newCourse.lot_tombola} onChange={e => setNewCourse({...newCourse, lot_tombola: e.target.checked})} /></td>
                  <td><button className="btn-icon success" onClick={addCourse}><Plus size={14}/></button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* POINTS CHRONO */}
      {activeSection === 'points' && (
        <div className="section">
          <div className="section-body">
            <table className="table">
              <thead>
                <tr><th>Nom affiche</th><th>Nom onglet Google Sheet</th><th>Ouverture</th><th>Fermeture</th><th>Sauver</th><th></th></tr>
              </thead>
              <tbody>
                {points.map(p => (
                  <tr key={p.id}>
                    <td><input defaultValue={p.nom} onBlur={async e => { if (e.target.value !== p.nom) { await supabase.from('points_chrono').update({ nom: e.target.value }).eq('id', p.id); fetchAll() } }} /></td>
                    <td><input defaultValue={p.nom_feuille} onBlur={async e => { if (e.target.value !== p.nom_feuille) { await supabase.from('points_chrono').update({ nom_feuille: e.target.value }).eq('id', p.id); fetchAll() } }} /></td>
                    <td><input type="datetime-local" value={getHoraireEdit(p.id, 'heure_ouverture')} onChange={e => setHoraireEdit(p.id, 'heure_ouverture', e.target.value)} /></td>
                    <td><input type="datetime-local" value={getHoraireEdit(p.id, 'heure_fermeture')} onChange={e => setHoraireEdit(p.id, 'heure_fermeture', e.target.value)} /></td>
                    <td>
                      <button className={'btn-icon ' + (horaireModifie(p.id) ? 'success' : '')}
                        style={!horaireModifie(p.id) ? {opacity:0.3, cursor:'default'} : {}}
                        onClick={() => horaireModifie(p.id) && sauvegarderHoraires(p.id)}>
                        <Save size={14}/>
                      </button>
                    </td>
                    <td><button className="btn-icon danger" onClick={() => deletePoint(p.id)}><Trash2 size={14}/></button></td>
                  </tr>
                ))}
                <tr className="add-row">
                  <td><input placeholder="Nom affiche *" value={newPoint.nom} onChange={e => setNewPoint({...newPoint, nom: e.target.value})} /></td>
                  <td><input placeholder="Nom onglet exact *" value={newPoint.nom_feuille} onChange={e => setNewPoint({...newPoint, nom_feuille: e.target.value})} /></td>
                  <td><input type="datetime-local" value={newPoint.heure_ouverture} onChange={e => setNewPoint({...newPoint, heure_ouverture: e.target.value})} /></td>
                  <td><input type="datetime-local" value={newPoint.heure_fermeture} onChange={e => setNewPoint({...newPoint, heure_fermeture: e.target.value})} /></td>
                  <td colSpan={2}><button className="btn-icon success" onClick={addPoint}><Plus size={14}/></button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* POINTS PAR COURSE */}
      {activeSection === 'liaisons' && (
        <div className="section">
          <div className="section-body">
            {courses.map(course => {
              const pts = coursePoints.filter(cp => cp.course_id === course.id).sort((a, b) => a.ordre - b.ordre)
              return (
                <div key={course.id} className="course-points-group">
                  <h3>{course.nom}</h3>
                  <div className="points-list">
                    {pts.length === 0 && <span className="empty">Aucun point configure</span>}
                    {pts.map(cp => (
                      <div key={cp.id} className="point-badge">
                        <input type="number" min="1" defaultValue={cp.ordre}
                          style={{width:36, padding:'2px 4px', border:'1px solid #b2d8cc', borderRadius:4, fontSize:12, textAlign:'center'}}
                          onBlur={e => updateCoursePointOrdre(cp.id, e.target.value)} />
                        {cp.points_chrono?.nom}
                        <button onClick={() => deleteCoursePoint(cp.id)}><Trash2 size={12}/></button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
            <div className="add-course-point">
              <select value={newCoursePoint.course_id} onChange={e => setNewCoursePoint({...newCoursePoint, course_id: e.target.value})}>
                <option value="">Course...</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
              <select value={newCoursePoint.point_chrono_id} onChange={e => setNewCoursePoint({...newCoursePoint, point_chrono_id: e.target.value})}>
                <option value="">Point chrono...</option>
                {points.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <input type="number" min="1" value={newCoursePoint.ordre}
                onChange={e => setNewCoursePoint({...newCoursePoint, ordre: parseInt(e.target.value)})}
                style={{width: 60}} placeholder="Ordre" />
              <button className="btn success" onClick={addCoursePoint}><Plus size={14}/> Ajouter</button>
            </div>
          </div>
        </div>
      )}

      {/* SECURITE */}
      {activeSection === 'securite' && (
        <div className="section">
          <div className="section-body">
            <p style={{fontSize:13, color:'#888', marginBottom:16}}>
              Les PINs sont stockes de facon securisee (hash SHA-256). Minimum 4 chiffres.
            </p>

            {pinMsg && (
              <div style={{
                padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13, fontWeight:600,
                background: pinMsg.type === 'success' ? '#e8f5f0' : '#fee',
                color: pinMsg.type === 'success' ? '#1a7a5e' : '#c00',
                border: '1px solid ' + (pinMsg.type === 'success' ? '#b2d8cc' : '#fcc')
              }}>
                {pinMsg.text}
              </div>
            )}

            <div style={{display:'flex', flexDirection:'column', gap:24, maxWidth:420}}>

              {/* PIN Benevole */}
              <div>
                <label style={{display:'block', fontWeight:600, marginBottom:8}}>PIN Benevole</label>
                <div style={{display:'flex', gap:8}}>
                  <div style={{position:'relative', flex:1}}>
                    <input
                      type={showPinB ? 'text' : 'password'}
                      inputMode="numeric"
                      placeholder="Nouveau PIN benevole"
                      value={pinBenevole}
                      onChange={e => { if (/^\d*$/.test(e.target.value) && e.target.value.length <= 6) setPinBenevole(e.target.value) }}
                      style={{width:'100%', padding:'8px 36px 8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:18, letterSpacing:6}}
                    />
                    <button type="button" onClick={() => setShowPinB(!showPinB)}
                      style={{position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', border:'none', background:'none', cursor:'pointer', color:'#888'}}>
                      {showPinB ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                  <button className="btn success" onClick={() => sauvegarderPin('pin_benevole', pinBenevole)} disabled={savingPin || pinBenevole.length < 4}>
                    <Save size={14}/> Sauver
                  </button>
                </div>
              </div>

              {/* PIN Admin */}
              <div>
                <label style={{display:'block', fontWeight:600, marginBottom:8}}>PIN Admin</label>
                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                  <div style={{position:'relative'}}>
                    <input
                      type={showPinA ? 'text' : 'password'}
                      inputMode="numeric"
                      placeholder="Nouveau PIN admin"
                      value={pinAdmin}
                      onChange={e => { if (/^\d*$/.test(e.target.value) && e.target.value.length <= 6) setPinAdmin(e.target.value) }}
                      style={{width:'100%', padding:'8px 36px 8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:18, letterSpacing:6}}
                    />
                    <button type="button" onClick={() => setShowPinA(!showPinA)}
                      style={{position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', border:'none', background:'none', cursor:'pointer', color:'#888'}}>
                      {showPinA ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                  <input
                    type="password"
                    inputMode="numeric"
                    placeholder="Confirmer le PIN admin"
                    value={pinAdminConfirm}
                    onChange={e => { if (/^\d*$/.test(e.target.value) && e.target.value.length <= 6) setPinAdminConfirm(e.target.value) }}
                    style={{width:'100%', padding:'8px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:18, letterSpacing:6}}
                  />
                  <button className="btn success" style={{alignSelf:'flex-start'}}
                    onClick={() => sauvegarderPin('pin_admin', pinAdmin, pinAdminConfirm)}
                    disabled={savingPin || pinAdmin.length < 4 || pinAdmin !== pinAdminConfirm}>
                    <Save size={14}/> Sauver PIN Admin
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  )
}

